import React, { useState } from 'react';
import {
  TextField,
  FormControl,
  FormHelperText,
  Box,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from '@material-ui/core';
import { IconContext } from 'react-icons';
import { MdExpandMore } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';

import { DSLink, DSText } from '@engenharia/ds-react';

import Logo from '../../components/molecules/Logo';
import MetaHeader from '../../components/MetaHeader';
import { Container, Card, CardHeader, Form } from './styles';
import { mailValidation } from '../../utils/validations';
import api from '../../services/api';
import Notification from '../../components/Notification';
import Button from '../../components/atoms/Button';
import Header from '../../components/Header';
import { transcodeLanguage } from '../../utils/language';
import { getLanguage } from '../../services/auth';

export default function ConfirmEmail() {
  const { t } = useTranslation();
  const history = useHistory();

  const [state, setState] = useState({
    email: '',
    emailValidation: {},
  });

  const [expanded, setExpanded] = React.useState('');
  const [notification, setNotification] = useState({});

  const handleExpansionPanelChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleEmailChange = (event) => {
    setState({
      ...state,
      email: event.target.value,
      emailValidation: mailValidation(event.target.value),
    });
  };

  const clickActionButton = (link) => {
    history.push(link);
  };

  const notificationButton = (options) => (
    <DSLink onClick={() => clickActionButton(options.link)} variant="button" color="secondary">
      {options.message}
    </DSLink>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { email } = state;
    try {
      if (!email) {
        setNotification({ key: new Date().getTime(), message: t('errors.email-invalid') });
      }

      const actionButton = notificationButton({
        message: t('confirmation-email.link-login'),
        link: '/',
      });

      const response = await api.get('/accounts/send_confirmation', {
        params: {
          email,
          language: transcodeLanguage(getLanguage()),
        },
      });

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message,
        button: actionButton,
      });
    } catch (error) {
      const { response } = error;

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const expandMlabsIcon = () => (
    <IconContext.Provider value={{ color: '#1a73e8' }}>
      <div>
        <MdExpandMore />
      </div>
    </IconContext.Provider>
  );

  return (
    <>
      <Header positionFixed />
      <Container>
        <MetaHeader
          title="Não recebeu o e-mail de confirmação? - mLabs | Gestão de redes sociais"
          description="mLabs - Gestão de Redes sociais | E-mail de confirmação"
        />
        <Box>
          <Logo />
          <Box my={8} display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center">
            <Box mx={2} component={Card}>
              <CardHeader>
                <DSText variant="h4" weight={400} color="primary">
                  {t('confirmation-email.title')}
                </DSText>
              </CardHeader>
              <DSText variant="body2" weight={400} color="textSecondary">
                {t('confirmation-email.description')}
              </DSText>
              <Form onSubmit={handleSubmit}>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    required
                    type="email"
                    id="email"
                    name="email"
                    data-testid="formEmail"
                    label="Seu e-mail"
                    value={state.email}
                    onChange={handleEmailChange}
                    variant="outlined"
                    autoComplete="email"
                    margin="normal"
                    autoFocus
                    error={state.emailValidation?.emailInvalidError}
                  />

                  {!state.emailValidation.emailInvalidError && (
                    <FormHelperText id="email-error">
                      {t('confirmation-email.email-helper')}
                    </FormHelperText>
                  )}
                  {state.emailValidation.emailInvalidError && (
                    <FormHelperText error id="email-error">
                      {t('errors.email-invalid')}
                    </FormHelperText>
                  )}
                  <Box display="flex" alignSelf="flex-end">
                    <Button
                      mt={4}
                      type="submit"
                      id="btnSubmit"
                      data-testid="formSubmit"
                      color="primary"
                      startIcon={<FiMail />}
                    >
                      {t('confirmation-email.button.send')}
                    </Button>
                  </Box>
                </FormControl>
              </Form>
              <Box display="flex" alignSelf="flex-end">
                <DSLink
                  variant="button"
                  underline="always"
                  href="/accounts/sign_in"
                  color="secondary"
                >
                  {t('confirmation-email.link-login')}
                </DSLink>
              </Box>
            </Box>
            <Box mx={2} component={Card}>
              <CardHeader>
                <DSText variant="h4" weight={400}>
                  {t('confirmation-email.title2')}
                </DSText>
              </CardHeader>
              <div>
                <ExpansionPanel
                  square
                  expanded={expanded === 'panel1'}
                  onChange={handleExpansionPanelChange('panel1')}
                >
                  <ExpansionPanelSummary
                    aria-controls="panel1d-content"
                    id="panel1d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      O e-mail de confirmação de conta não chegou.
                    </DSText>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <DSText variant="body2" weight={400}>
                      R. Se você não recebeu o e-mail para confirmação da sua conta mLabs, verifique
                      sua caixa de spam e lixo eletrônico. Caso o e-mail realmente não tenha
                      chegado, utilize o reenvio de e-mail, no campo de texto ao lado esquerdo desta
                      pagina.
                      <br />
                      <br />
                      Se, mesmo com o reenvio, você ainda não tiver recebido o e-mail, entre em
                      contato com o nosso time de suporte pelo chat da mLabs ou{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        envie uma mensagem para nós
                      </DSLink>
                      .
                    </DSText>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
                <ExpansionPanel
                  square
                  expanded={expanded === 'panel2'}
                  onChange={handleExpansionPanelChange('panel2')}
                >
                  <ExpansionPanelSummary
                    aria-controls="panel2d-content"
                    id="panel2d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      Não me lembro qual é o e-mail do cadastro.
                    </DSText>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <DSText variant="body2" weight={400}>
                      R. Se você não está certo de qual e-mail usou para se criar sua conta mLabs
                      sugerimos que, primeiro, você solicite o reenvio do e-mail de confirmação para
                      os principais e-mails que utiliza. Caso o e-mail não esteja cadastrado na
                      mLabs, a solicitação simplesmente será ignorada.
                      <br />
                      <br />
                      Se mesmo assim você não descobrir qual e-mail utilizou para o cadastro, por
                      favor entre em contato com o{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        atendimento da mLabs
                      </DSLink>
                      .
                    </DSText>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
                <ExpansionPanel
                  square
                  expanded={expanded === 'panel3'}
                  onChange={handleExpansionPanelChange('panel3')}
                >
                  <ExpansionPanelSummary
                    aria-controls="panel3d-content"
                    id="panel3d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      Me cadastrei pelo botão do Facebook e não pediu e-mail.
                    </DSText>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <DSText variant="body2" weight={400}>
                      R. Se você se cadastrou na mLabs utilizando o botão do Facebook, não é
                      necessária a confirmação de e-mail. Se ainda assim tiver problemas para
                      acessar sua conta, entre em contato com o{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        time de atendimento da mLabs
                      </DSLink>
                      .
                    </DSText>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
                <ExpansionPanel
                  square
                  expanded={expanded === 'panel4'}
                  onChange={handleExpansionPanelChange('panel4')}
                >
                  <ExpansionPanelSummary
                    aria-controls="panel4d-content"
                    id="panel4d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      Socorro! Já tentei de tudo :(
                    </DSText>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    <DSText variant="body2" weight={400}>
                      R. Se nenhuma das questões anteriores te ajudou a resolver seu problema, por
                      favor entre em contato com nosso{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        time de atendimento
                      </DSLink>
                      .Teremos prazer em te ajudar!
                    </DSText>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              </div>
            </Box>
          </Box>
        </Box>
        {notification?.key && (
          <Notification
            index={notification.key}
            message={notification.message}
            button={notification.button}
          />
        )}
      </Container>
    </>
  );
}
